// Sale unit access for the public /forsale pages.
//
// These used to be a hardcoded array in this file plus JPEGs committed under
// /assets/local/forsale, which meant a branch, a PR, CI and a production release to sell a
// machine. They are Firestore records now, served by the maintenance service, and the
// photographs live in a public Cloud Storage bucket.
//
// The privacy contract that used to live at the top of this file has not gone away - it moved
// to where it can be enforced instead of remembered. `service/src/schemas.ts` has no field for
// an owner, a seller, an address, or a serial, so a listing cannot carry one; the shapers
// beside `machineTags` in `service/src/portal.ts` are field-by-field, so the internal `ref`,
// the per-claim sourcing, and every withheld frame's reason cannot cross to this side. The
// reasoning is in planning/firesale-listings-as-data-plan.md and the workflow that produces a
// listing is the `firesale-listing` skill.
//
// What this file still owes the pages:
//
//   * an EMPTY directory rather than an error. A page saying "nothing is listed at the moment"
//     is a true statement about a quiet week; a page saying "we could not reach the server" is
//     a broken company. The distinction matters and both callers rely on it.
//   * `findListing` is ASYNC now. It was synchronous when the array was compiled in, and the
//     two views that call it were written around that.

const API = {
  saleUnits: '/api/v2/sale-units',
};

async function getJson(url) {
  const response = await fetch(url, { cache: 'no-store' });
  const body = await response.json().catch(() => ({}));
  return { status: response.status, ok: response.ok, body };
}

/**
 * Directory tiles: the machine, its hero photograph, and the two numbers every caller asks
 * first - hours and asking price. The full-bleed tile design (C3, Daniel 2026-08-30) is the
 * photo IS the card, text on a scrim at the bottom.
 *
 * The ORDER is the server's, and deliberately so: live listings newest first, then the sold
 * ones. Sorting again here would let the page and the record disagree about a rule Daniel
 * asked for by name.
 */
export async function loadSaleUnitTiles() {
  return (await loadSaleUnitTilesResult()).tiles;
}

export async function loadSaleUnitTilesResult(after) {
  try {
    const { ok, body } = await getJson(API.saleUnits + (after ? `?after=${encodeURIComponent(after)}` : ''));
    if (!ok || !Array.isArray(body.listings)) {
      return { tiles: [], error: 'We could not load the machines just now. Please try again.' };
    }
    return { tiles: body.listings, next: body.next };
  } catch {
    return { tiles: [], error: 'We could not reach the server. Please check your connection and try again.' };
  }
}

/**
 * One listing.
 *
 * Three outcomes, and the caller has to tell them apart: the listing (`listing`), a machine
 * that sold and was taken down (`gone`, which the server answers 410 to), and everything else.
 * A sold-and-archived URL is not a 404 on purpose - the link is out in the world either way,
 * and "this machine has sold" is a better answer than a dead page.
 *
 * Async, unlike the version that read a compiled-in array. Both callers await it.
 */
export async function findListing(slug) {
  const key = String(slug || '').trim().toLowerCase();
  if (!key) return { listing: null };
  try {
    const { status, ok, body } = await getJson(`${API.saleUnits}/${encodeURIComponent(key)}`);
    if (ok && body.listing) return { listing: body.listing };
    if (status === 410) return { listing: null, gone: true };
    if (status === 404) return { listing: null };
    return { listing: null, error: 'We could not load this machine just now. Please try again.' };
  } catch {
    return { listing: null, error: 'We could not reach the server. Please check your connection and try again.' };
  }
}
